<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';

const props = defineProps({
  position: { type: Object, required: true }, // {x, y, parentStep, branchKey}
  canConnect: { type: Boolean, default: false }, // si hay steps disponibles para conectar
});
const emit = defineEmits(['pick', 'close', 'pick-connector']);

const search = ref('');
const cursor = ref(0);
const searchInput = ref(null);

// Definición visual de los 13 step types soportados, agrupados por categoría.
// shortcut: tecla rápida visible al hover.
const STEPS = [
  // Comunicación
  { key: 'message',       name: 'Mensaje',          desc: 'Texto, plantillas, variables',     cat: 'comm',  gradient: 'linear-gradient(135deg, #60a5fa, #3b82f6)', iconText: '🤖', hoverBg: 'hover:bg-blue-50 dark:hover:bg-blue-500/10',  shortcut: 'M' },
  { key: 'add_label',     name: 'Etiqueta',         desc: 'Agregar al lead automáticamente',  cat: 'comm',  gradient: 'linear-gradient(135deg, #34d399, #10b981)', iconText: '🏷', white: true, hoverBg: 'hover:bg-emerald-50 dark:hover:bg-emerald-500/10', shortcut: 'E', badge: { label: 'NUEVO', color: 'bg-emerald-500 text-white' } },
  { key: 'reaction',      name: 'Reacción',         desc: 'Emoji al cliente',                 cat: 'comm',  gradient: 'linear-gradient(135deg, #fb7185, #ef4444)', iconText: '❤', white: true, hoverBg: 'hover:bg-rose-50 dark:hover:bg-rose-500/10',  shortcut: 'R' },
  { key: 'comment',       name: 'Comentario',       desc: 'Nota privada al equipo',           cat: 'comm',  gradient: 'linear-gradient(135deg, #f472b6, #ec4899)', iconText: '💬', white: true, hoverBg: 'hover:bg-pink-50 dark:hover:bg-pink-500/10' },
  { key: 'list_message',  name: 'List Message',     desc: 'Lista interactiva',                cat: 'comm',  gradient: 'linear-gradient(135deg, #34d399, #25D366)', iconText: '≡', white: true, hoverBg: 'hover:bg-emerald-50 dark:hover:bg-emerald-500/10', badge: { label: 'WhatsApp', color: 'bg-emerald-100 text-emerald-700' } },

  // Lógica & control
  { key: 'pause',         name: 'Pausa',            desc: 'Espera antes del siguiente paso',  cat: 'logic', gradient: 'linear-gradient(135deg, #f9a8d4, #ec4899)', iconText: '⏸', white: true, hoverBg: 'hover:bg-pink-50 dark:hover:bg-pink-500/10', shortcut: 'P' },
  { key: 'condition',     name: 'Condición',        desc: 'Ramifica el flujo (Sí / No)',      cat: 'logic', gradient: 'linear-gradient(135deg, #a78bfa, #8b5cf6)', iconText: '◇', white: true, hoverBg: 'hover:bg-violet-50 dark:hover:bg-violet-500/10', shortcut: 'C' },
  { key: 'validation',    name: 'Validación',       desc: 'Espera respuesta válida',          cat: 'logic', gradient: 'linear-gradient(135deg, #6ee7b7, #10b981)', iconText: '✓', white: true, hoverBg: 'hover:bg-emerald-50 dark:hover:bg-emerald-500/10' },
  { key: 'action',        name: 'Acciones',         desc: 'Asignar, etiquetar, mover etapa…', cat: 'logic', gradient: 'linear-gradient(135deg, #6ee7b7, #10b981)', iconText: '✏', white: true, hoverBg: 'hover:bg-emerald-50 dark:hover:bg-emerald-500/10', dropdown: true },
  { key: 'round_robin',   name: 'Round Robin',      desc: 'Distribuir entre agentes',         cat: 'logic', gradient: 'linear-gradient(135deg, #fde68a, #f59e0b)', iconText: '↻', white: true, hoverBg: 'hover:bg-yellow-50 dark:hover:bg-yellow-500/10' },

  // Flujo
  { key: 'subbot',        name: 'Iniciar Salesbot', desc: 'Lanzar otro bot anidado',          cat: 'flow',  gradient: 'linear-gradient(135deg, #34d399, #10b981)', iconText: '▶', white: true, hoverBg: 'hover:bg-emerald-50 dark:hover:bg-emerald-500/10' },

  // Avanzado
  { key: 'subscribe_meta',name: 'Suscribirse',      desc: 'Difusiones de WhatsApp/IG',        cat: 'adv',   gradient: 'linear-gradient(135deg, #60a5fa, #0866FF)', iconText: '∞', white: true, hoverBg: 'hover:bg-blue-50 dark:hover:bg-blue-500/10', badge: { label: 'Meta', color: 'bg-blue-100 text-blue-700' } },
  { key: 'code',          name: 'Paso personalizado',desc: 'Código Ruby con timeout 5s',      cat: 'adv',   gradient: 'linear-gradient(135deg, #60a5fa, #3b82f6)', iconText: '</>', white: true, mono: true, hoverBg: 'hover:bg-blue-50 dark:hover:bg-blue-500/10' },
  { key: 'widget',        name: 'Widgets',          desc: 'Calendario, formulario, pago',     cat: 'adv',   gradient: 'linear-gradient(135deg, #67e8f9, #06b6d4)', iconText: '▦', white: true, hoverBg: 'hover:bg-cyan-50 dark:hover:bg-cyan-500/10' },
];

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return STEPS;
  return STEPS.filter(s => s.name.toLowerCase().includes(q) || s.desc.toLowerCase().includes(q) || (s.shortcut || '').toLowerCase() === q);
});

// Cuando NO hay búsqueda, agrupamos por categoría. Cuando sí, lista plana.
const grouped = computed(() => {
  if (search.value) return null;
  const groups = { comm: [], logic: [], flow: [], adv: [] };
  filtered.value.forEach(s => groups[s.cat].push(s));
  return groups;
});

const flatItems = computed(() => filtered.value); // para keyboard nav

// V6 · 2026-05-14 (Victor) · Submenú de Acciones.
// Cuando "Acciones" (dropdown:true) se clickea, en lugar de emit('pick','action')
// directo (que ponía add_label por default), mostramos un submenú con los 8
// action_types soportados por Bots::ActionExecutor. El user elige y emitimos
// 'pick' con la key 'action__<subtype>' para que el Editor sepa qué config aplicar.
const ACTION_SUBTYPES = [
  { key: 'add_label',       name: 'Agregar etiqueta',     icon: '🏷',  gradient: 'linear-gradient(135deg, #34d399, #10b981)' },
  { key: 'remove_label',    name: 'Quitar etiqueta',      icon: '🚫', gradient: 'linear-gradient(135deg, #f87171, #ef4444)' },
  { key: 'assign_agent',    name: 'Asignar agente',       icon: '👤', gradient: 'linear-gradient(135deg, #60a5fa, #3b82f6)' },
  { key: 'assign_team',     name: 'Asignar equipo',       icon: '👥', gradient: 'linear-gradient(135deg, #a78bfa, #8b5cf6)' },
  { key: 'change_stage',    name: 'Cambiar etapa',        icon: '↻',  gradient: 'linear-gradient(135deg, #fde68a, #f59e0b)' },
  { key: 'change_priority', name: 'Cambiar prioridad',    icon: '⚡', gradient: 'linear-gradient(135deg, #fb7185, #ec4899)' },
  { key: 'resolve',         name: 'Resolver conversación',icon: '✓',  gradient: 'linear-gradient(135deg, #6ee7b7, #059669)' },
  { key: 'snooze',          name: 'Posponer',             icon: '💤', gradient: 'linear-gradient(135deg, #93c5fd, #6366f1)' },
];
const showActionMenu = ref(false);
function pick(stepKey) {
  // Si es la entry "action" (Acciones), abrir submenú en vez de emitir
  if (stepKey === 'action') {
    showActionMenu.value = true;
    return;
  }
  emit('pick', stepKey);
}
function pickAction(subKey) {
  showActionMenu.value = false;
  emit('pick', `action__${subKey}`);
}
function closeActionMenu() { showActionMenu.value = false; }
function close() { emit('close'); }

function onKeydown(ev) {
  if (ev.key === 'ArrowDown') {
    cursor.value = (cursor.value + 1) % flatItems.value.length;
    ev.preventDefault();
  } else if (ev.key === 'ArrowUp') {
    cursor.value = (cursor.value - 1 + flatItems.value.length) % flatItems.value.length;
    ev.preventDefault();
  } else if (ev.key === 'Enter') {
    const item = flatItems.value[cursor.value];
    if (item) pick(item.key);
    ev.preventDefault();
  } else if (ev.key === 'Escape') {
    close();
    ev.preventDefault();
  }
}

// Posicionamiento — si la pos x desbordaría a la derecha, la corremos
const popoverStyle = computed(() => {
  const W = 360;
  const H = 540;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const x = Math.min(props.position.x, vw - W - 12);
  const y = Math.min(props.position.y, vh - H - 12);
  return { left: Math.max(8, x) + 'px', top: Math.max(8, y) + 'px', width: W + 'px' };
});

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
  nextTick(() => searchInput.value?.focus());
});
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));
</script>

<template>
  <div class="fixed inset-0 z-40" @click="close">
    <div
      class="fixed z-50 rounded-xl shadow-2xl border border-n-weak overflow-hidden bg-white/85 dark:bg-slate-900/85"
      :style="{ ...popoverStyle, backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)' }"
      @click.stop
      @wheel.stop
    >
      <!-- Header -->
      <div class="px-4 pt-3 pb-2">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-[13px] text-n-slate-12 font-semibold">Agregar paso</h3>
          <kbd class="text-[10px] px-1.5 py-0.5 rounded bg-n-alpha-black2 text-n-slate-11 font-mono">⌘K</kbd>
        </div>
        <input
          ref="searchInput"
          v-model="search"
          type="text"
          placeholder="Buscar paso…"
          class="w-full text-xs px-3 py-2 rounded-lg bg-n-alpha-black1 border border-n-weak text-n-slate-12 focus:outline-none focus:border-blue-400 placeholder-n-slate-11"
          @keydown.stop="onKeydown"
        />
      </div>

      <!-- Lista (grouped si no hay search, flat si sí) -->
      <div class="px-2 pb-2 max-h-[440px] overflow-y-auto">

        <!-- ✨ Nuevo: Agregar conector (solo si hay steps disponibles) -->
        <button
          v-if="canConnect && !search"
          class="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left group transition mt-1
                 bg-gradient-to-b from-emerald-50 to-emerald-100 hover:from-emerald-100 hover:to-emerald-200
                 dark:from-emerald-500/10 dark:to-emerald-500/20"
          @click="emit('pick-connector')"
        >
          <span class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 shadow-sm bg-emerald-500 text-white">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round">
              <path d="M10 14a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5"/>
              <path d="M14 10a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5"/>
            </svg>
          </span>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-emerald-900 dark:text-emerald-300 leading-tight inline-flex items-center gap-1.5">
              Agregar conector
              <span class="text-[8px] px-1 py-0.5 rounded font-semibold bg-emerald-700 text-white">NUEVO</span>
            </div>
            <div class="text-[10px] text-emerald-700 dark:text-emerald-400 leading-tight">
              Arrastra una línea hacia otro paso para conectarlo
            </div>
          </div>
          <kbd class="text-[9px] text-emerald-700 font-mono opacity-0 group-hover:opacity-100">L</kbd>
        </button>

        <template v-if="grouped">
          <template v-if="grouped.comm.length">
            <div class="text-[9px] uppercase tracking-wider text-n-slate-11 font-semibold px-2 pt-2 pb-1">Comunicación</div>
            <button
              v-for="(s, idx) in grouped.comm"
              :key="s.key"
              class="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left group transition"
              :class="[s.hoverBg, flatItems.indexOf(s) === cursor ? 'ring-1 ring-blue-300' : '']"
              @click="pick(s.key)"
              @mouseenter="cursor = flatItems.indexOf(s)"
            >
              <span class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 shadow-sm" :style="{ background: s.gradient }">
                <span :class="[s.white ? 'text-white' : '', s.mono ? 'font-mono text-[11px]' : 'text-base']" :style="{ fontSize: s.mono ? '10px' : '15px' }">{{ s.iconText }}</span>
              </span>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-n-slate-12 leading-tight inline-flex items-center gap-1.5">
                  {{ s.name }}
                  <span v-if="s.dropdown" class="text-n-slate-11 text-[10px]">▾</span>
                  <span v-if="s.badge" class="text-[9px] px-1 py-0.5 rounded font-medium" :class="s.badge.color">{{ s.badge.label }}</span>
                </div>
                <div class="text-[10px] text-n-slate-11 leading-tight">{{ s.desc }}</div>
              </div>
              <kbd v-if="s.shortcut" class="text-[9px] text-n-slate-11 font-mono opacity-0 group-hover:opacity-100">{{ s.shortcut }}</kbd>
            </button>
          </template>

          <template v-if="grouped.logic.length">
            <div class="text-[9px] uppercase tracking-wider text-n-slate-11 font-semibold px-2 pt-2 pb-1">Lógica & control</div>
            <button
              v-for="s in grouped.logic"
              :key="s.key"
              class="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left group transition"
              :class="[s.hoverBg, flatItems.indexOf(s) === cursor ? 'ring-1 ring-blue-300' : '']"
              @click="pick(s.key)"
              @mouseenter="cursor = flatItems.indexOf(s)"
            >
              <span class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 shadow-sm" :style="{ background: s.gradient }">
                <span :class="[s.white ? 'text-white' : '', s.mono ? 'font-mono' : '']" :style="{ fontSize: s.mono ? '10px' : '15px' }">{{ s.iconText }}</span>
              </span>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-n-slate-12 leading-tight inline-flex items-center gap-1.5">
                  {{ s.name }}
                  <span v-if="s.dropdown" class="text-n-slate-11 text-[10px]">▾</span>
                  <span v-if="s.badge" class="text-[9px] px-1 py-0.5 rounded font-medium" :class="s.badge.color">{{ s.badge.label }}</span>
                </div>
                <div class="text-[10px] text-n-slate-11 leading-tight">{{ s.desc }}</div>
              </div>
              <kbd v-if="s.shortcut" class="text-[9px] text-n-slate-11 font-mono opacity-0 group-hover:opacity-100">{{ s.shortcut }}</kbd>
            </button>
          </template>

          <template v-if="grouped.flow.length">
            <div class="text-[9px] uppercase tracking-wider text-n-slate-11 font-semibold px-2 pt-2 pb-1">Flujo</div>
            <button
              v-for="s in grouped.flow"
              :key="s.key"
              class="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left group transition"
              :class="[s.hoverBg, flatItems.indexOf(s) === cursor ? 'ring-1 ring-blue-300' : '']"
              @click="pick(s.key)"
              @mouseenter="cursor = flatItems.indexOf(s)"
            >
              <span class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 shadow-sm" :style="{ background: s.gradient }">
                <span :class="[s.white ? 'text-white' : '', s.mono ? 'font-mono' : '']" :style="{ fontSize: s.mono ? '10px' : '15px' }">{{ s.iconText }}</span>
              </span>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-n-slate-12 leading-tight inline-flex items-center gap-1.5">
                  {{ s.name }}
                  <span v-if="s.badge" class="text-[9px] px-1 py-0.5 rounded font-medium" :class="s.badge.color">{{ s.badge.label }}</span>
                </div>
                <div class="text-[10px] text-n-slate-11 leading-tight">{{ s.desc }}</div>
              </div>
            </button>
          </template>

          <template v-if="grouped.adv.length">
            <div class="border-t border-n-weak my-1.5"></div>
            <div class="text-[9px] uppercase tracking-wider text-n-slate-11 font-semibold px-2 pt-1 pb-1">Avanzado</div>
            <button
              v-for="s in grouped.adv"
              :key="s.key"
              class="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left group transition"
              :class="[s.hoverBg, flatItems.indexOf(s) === cursor ? 'ring-1 ring-blue-300' : '']"
              @click="pick(s.key)"
              @mouseenter="cursor = flatItems.indexOf(s)"
            >
              <span class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 shadow-sm" :style="{ background: s.gradient }">
                <span :class="[s.white ? 'text-white' : '', s.mono ? 'font-mono' : '']" :style="{ fontSize: s.mono ? '10px' : '15px' }">{{ s.iconText }}</span>
              </span>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-n-slate-12 leading-tight inline-flex items-center gap-1.5">
                  {{ s.name }}
                  <span v-if="s.badge" class="text-[9px] px-1 py-0.5 rounded font-medium" :class="s.badge.color">{{ s.badge.label }}</span>
                </div>
                <div class="text-[10px] text-n-slate-11 leading-tight">{{ s.desc }}</div>
              </div>
            </button>
          </template>
        </template>

        <!-- Search activa: lista plana -->
        <template v-else>
          <button
            v-for="s in filtered"
            :key="s.key"
            class="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left group transition"
            :class="[s.hoverBg, flatItems.indexOf(s) === cursor ? 'ring-1 ring-blue-300' : '']"
            @click="pick(s.key)"
            @mouseenter="cursor = flatItems.indexOf(s)"
          >
            <span class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 shadow-sm" :style="{ background: s.gradient }">
              <span :class="[s.white ? 'text-white' : '', s.mono ? 'font-mono' : '']" :style="{ fontSize: s.mono ? '10px' : '15px' }">{{ s.iconText }}</span>
            </span>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-n-slate-12 leading-tight">{{ s.name }}</div>
              <div class="text-[10px] text-n-slate-11 leading-tight">{{ s.desc }}</div>
            </div>
          </button>
          <div v-if="!filtered.length" class="text-center text-xs text-n-slate-11 py-6">
            Sin resultados para "<strong>{{ search }}</strong>"
          </div>
        </template>
      </div>

      <!-- Footer atajos -->
      <div class="text-[10px] text-n-slate-11 px-3 py-2 border-t border-n-weak flex justify-between bg-n-alpha-black1">
        <span>↑↓ navegar</span>
        <span>↵ seleccionar</span>
        <span>esc cerrar</span>
      </div>
    </div>

    <!-- V6 · Submenú de Acciones (Victor 2026-05-14) -->
    <div
      v-if="showActionMenu"
      class="fixed inset-0 z-[101] flex items-center justify-center"
      style="background: rgba(0,0,0,0.55); backdrop-filter: blur(6px);"
      @click.self="closeActionMenu"
    >
      <div
        class="w-[420px] rounded-xl overflow-hidden"
        style="background: linear-gradient(135deg,#0a0a0a,#050505); border: 1px solid rgba(251,191,36,0.30); box-shadow: 0 24px 70px rgba(0,0,0,0.7), 0 0 30px rgba(251,191,36,0.15);"
      >
        <div class="px-5 py-4 flex items-center gap-3" style="border-bottom: 1px solid rgba(255,255,255,0.06);">
          <span class="w-9 h-9 rounded-lg flex items-center justify-center" style="background: linear-gradient(135deg,#6ee7b7,#10b981);">
            <span class="text-white" style="font-size:16px;">✏</span>
          </span>
          <div class="flex-1">
            <div class="text-sm font-semibold text-white">Elegir tipo de acción</div>
            <div class="text-[11px]" style="color: rgba(255,255,255,0.55);">Selecciona qué hará este paso en el flujo del bot</div>
          </div>
          <button class="text-white/60 hover:text-white text-lg leading-none" @click="closeActionMenu">×</button>
        </div>

        <div class="p-2 max-h-[60vh] overflow-y-auto">
          <button
            v-for="a in ACTION_SUBTYPES"
            :key="a.key"
            class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition"
            style="color: rgba(255,255,255,0.88);"
            @mouseenter="$event.target.style.background='rgba(251,191,36,0.06)'"
            @mouseleave="$event.target.style.background='transparent'"
            @click="pickAction(a.key)"
          >
            <span class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" :style="{ background: a.gradient }">
              <span style="font-size: 16px;">{{ a.icon }}</span>
            </span>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-white">{{ a.name }}</div>
              <div class="text-[10px]" style="color: rgba(255,255,255,0.50);">{{ a.key }}</div>
            </div>
          </button>
        </div>

        <div class="px-4 py-2 text-[10px] flex justify-between" style="border-top: 1px solid rgba(255,255,255,0.06); color: rgba(255,255,255,0.45);">
          <span>Elige una acción para continuar</span>
          <span>esc cerrar</span>
        </div>
      </div>
    </div>
  </div>
</template>
